import React, { useState } from 'react';
import { Trash2, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface ExerciseFilterViewProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (muscles: string[], equipment: string[]) => void;
  initialMuscles: string[];
  initialEquipment: string[];
  availableMuscles: string[];
  availableEquipment: string[];
}

export function ExerciseFilterView({
  isOpen,
  onClose,
  onApply,
  initialMuscles,
  initialEquipment,
  availableMuscles,
  availableEquipment,
}: ExerciseFilterViewProps) {
  const { theme } = useTheme();
  const [filterTab, setFilterTab] = useState<'muscles' | 'equipment'>('muscles');
  const [pendingMuscles, setPendingMuscles] = useState<string[]>(initialMuscles);
  const [pendingEquipment, setPendingEquipment] = useState<string[]>(initialEquipment);

  // Reset pending state when opened
  React.useEffect(() => {
    if (isOpen) {
      setPendingMuscles(initialMuscles);
      setPendingEquipment(initialEquipment);
      setFilterTab('muscles');
    }
  }, [isOpen, initialMuscles, initialEquipment]);

  if (!isOpen) return null;

  const totalPending = pendingMuscles.length + pendingEquipment.length;

  return (
    <div className='fixed inset-0 z-[65] flex flex-col items-center justify-end'>
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />
      <div
        className={`relative w-full max-w-lg max-h-[80vh] flex flex-col rounded-t-2xl ${theme.card} border-t border-x ${theme.border} shadow-2xl`}
      >
        {/* Drag handle */}
        <div className='flex justify-center pt-3 pb-1'>
          <div className={`w-10 h-1 rounded-full ${theme.border} bg-current opacity-30`} />
        </div>

        {/* Header */}
        <div className={`flex items-center justify-between px-4 pb-3 border-b ${theme.border}`}>
          <h2 className='text-base font-semibold'>Filtros</h2>
          <button
            type='button'
            onClick={() => { setPendingMuscles([]); setPendingEquipment([]); }}
            className={`p-1.5 rounded-lg touch-manipulation ${totalPending > 0 ? 'text-red-500' : theme.textSecondary}`}
            aria-label='Limpiar filtros'
          >
            <Trash2 className='w-4 h-4' />
          </button>
        </div>

        {/* Tabs */}
        <div className='px-4 pt-4 pb-3'>
          <div className={`flex rounded-xl overflow-hidden border ${theme.border} p-0.5`}>
            <button
              type='button'
              onClick={() => setFilterTab('muscles')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                filterTab === 'muscles' ? 'bg-blue-600 text-white' : `${theme.textSecondary} ${theme.hover}`
              }`}
            >
              Músculos
              {pendingMuscles.length > 0 && <span className='ml-1.5'>({pendingMuscles.length})</span>}
            </button>
            <button
              type='button'
              onClick={() => setFilterTab('equipment')}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                filterTab === 'equipment' ? 'bg-blue-600 text-white' : `${theme.textSecondary} ${theme.hover}`
              }`}
            >
              Equipamiento
              {pendingEquipment.length > 0 && <span className='ml-1.5'>({pendingEquipment.length})</span>}
            </button>
          </div>
        </div>

        {/* List */}
        <div className='flex-1 overflow-y-auto px-4 pb-2 space-y-2'>
          {filterTab === 'muscles' && availableMuscles.map((m) => {
            const selected = pendingMuscles.includes(m);
            return (
              <button
                key={m}
                type='button'
                onClick={() => setPendingMuscles(prev => selected ? prev.filter(x => x !== m) : [...prev, m])}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-colors touch-manipulation text-left ${
                  selected ? 'border-blue-500 bg-blue-600/10' : `${theme.border} ${theme.hover}`
                }`}
              >
                <span className={`text-sm font-medium ${selected ? '' : theme.text}`}>{m}</span>
                {selected && (
                  <span className='w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0'>
                    <Check className='w-3.5 h-3.5 text-white' strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
          {filterTab === 'equipment' && availableEquipment.map((eq) => {
            const selected = pendingEquipment.includes(eq);
            return (
              <button
                key={eq}
                type='button'
                onClick={() => setPendingEquipment(prev => selected ? prev.filter(x => x !== eq) : [...prev, eq])}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-colors touch-manipulation text-left ${
                  selected ? 'border-blue-500 bg-blue-600/10' : `${theme.border} ${theme.hover}`
                }`}
              >
                <span className={`text-sm font-medium ${selected ? '' : theme.text}`}>{eq}</span>
                {selected && (
                  <span className='w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0'>
                    <Check className='w-3.5 h-3.5 text-white' strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Apply button */}
        <div className='px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]'>
          <button
            type='button'
            onClick={() => onApply(pendingMuscles, pendingEquipment)}
            className='w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 active:bg-blue-800 touch-manipulation'
          >
            Aplicar filtros
            {totalPending > 0 && (
              <span className='ml-1.5 text-blue-200 font-normal'>({totalPending})</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
